import { useCallback, useEffect, useRef, useState } from 'react'
import GeoJSON from 'geojson'

import { InjectionTokens } from '@/controller/tokens'

import { UniqueEntityID } from '@/domain/core'
import { UserGeometryChanged } from '@/domain/events/UserGeometryChanged'
import { UserGeometrySynced } from '@/domain/events/UserGeometrySynced'
import { Coordinates } from '@/domain/protocols/Coordinates'
import { UserGeometryInfo } from '@/domain/protocols/UserGeometry'
import { MapLayerState, MapLayerStateProps } from '@/domain/states/MapLayerState'
import {
  UserGeometryLayerVisibilityState,
  UserGeometryLayerVisibilityStateProps
} from '@/domain/states/UserGeometryLayerVisibilityState'

import { IUserGeometryRepository } from '@/data/UserGeometryRepository/IUserGeometryRepository'
import { readCachedFlatGeobufFeatureCollection } from '@/data/UserGeometryRepository/userGeometryFlatGeobuf'
import { computeGeoJsonBounds, GeoJsonBounds } from '@/data/UserGeometryRepository/userGeometryGeoJsonRender'

import { MapGeometries } from '../components/MapGeometries/MapGeometries'
import { buildUserGeometryRenderData, UserGeometryRenderData } from '../components/MapGeometries/userGeometryRenderData'
import { UserGeometryFocusRequestEvent } from '../interfaces/MapEvents'
import { MapImperativeInterface } from '../interfaces/MapImperativeInterface'
import { FeatureInfoState, FeatureInfoStateMutator } from '../states/FeatureInfoState'
import { getFeatureFlyToPadding, ScreenElementsStateMutator } from '../states/ScreenElementsState'

import type { ILogger } from '@/infra/logger/ILogger'
import { useInjection } from '@/presentation/contexts/InjectionContext'
import { useGlobalState } from '@/presentation/hooks/useGlobalState'
import { useI18n } from '@/presentation/hooks/useI18n'

function getSelectedFeatureId(): string | undefined {
  const active = FeatureInfoState.getValue().active
  if (active?.type !== 'user-geometry') return undefined
  return String(active.feature?.properties?.id ?? active.feature?.id ?? '')
}

function getFeatureId(feature: GeoJSON.Feature): string {
  return String(feature.properties?.id ?? feature.id ?? '')
}

function resolveFeatureTitle(feature: GeoJSON.Feature, geometryName: string, unnamedTitle: string): string {
  const name = feature.properties?.name
  if (typeof name === 'string' && name.trim() !== '') return name.trim()
  if (geometryName.trim() !== '') return geometryName.trim()
  return unnamedTitle
}

async function toUint8Array(blob: Uint8Array | Blob): Promise<Uint8Array> {
  if (blob instanceof Uint8Array) return blob
  return new Uint8Array(await blob.arrayBuffer())
}

// Reads a geometry's processed .fgb (via the LRU cache keyed by id:updatedAt)
// and builds its render-ready collections. `hiddenLayerIndices` are the runtime
// per-layer visibility overrides (T-D4): features at those indices are dropped
// before the render split. The filter is applied to a fresh copy so the cached
// collection is never mutated (the cache key ignores visibility). Returns null
// when the local file is missing.
async function loadUserGeometryRenderData(
  repository: IUserGeometryRepository,
  info: UserGeometryInfo,
  hiddenLayerIndices: number[]
): Promise<UserGeometryRenderData | null> {
  const id = new UniqueEntityID(info.id)

  const fileResult = await repository.getFile(id, 'processed')
  if (fileResult.isFailure) return null
  const file = fileResult.getValue()
  if (!file) return null

  const geometryResult = await repository.getGeometry(id)
  const updatedAt = geometryResult.isSuccess ? (geometryResult.getValue().updatedAt ?? info.id) : info.id

  const collection = await readCachedFlatGeobufFeatureCollection({
    id: info.id,
    updatedAt,
    readBytes: () => toUint8Array(file.blob)
  })

  const visibleCollection =
    hiddenLayerIndices.length === 0
      ? collection
      : {
          ...collection,
          features: collection.features.filter((_, index) => !hiddenLayerIndices.includes(index))
        }

  return buildUserGeometryRenderData(visibleCollection)
}

// Frames an extent: a degenerate (single-point) bound flies to its coordinates,
// anything with an area flies to its bounds.
function frameBounds(bounds: GeoJsonBounds, padding?: [number, number, number, number]): void {
  const [west, south] = bounds.sw
  const [east, north] = bounds.ne

  if (west === east && south === north) {
    MapImperativeInterface.flyToCoordinates(
      Coordinates.create({ longitude: west, latitude: south }),
      undefined,
      undefined,
      padding
    )
    return
  }

  MapImperativeInterface.flyToBounds([west, south, east, north], padding)
}

// Frames the selected feature (feature click): tight fit, no extra padding.
function frameFeature(feature: GeoJSON.Feature): void {
  const bounds = computeGeoJsonBounds({ type: 'FeatureCollection', features: [feature] })
  if (!bounds) return

  frameBounds(bounds)
}

function MapUserGeometriesPresenter() {
  const { t } = useI18n()
  const [mapLayerState, mapLayerStateMutator] = useGlobalState<MapLayerState, MapLayerStateProps>(
    InjectionTokens.MapLayerState
  )
  const [layerVisibility, layerVisibilityState] = useGlobalState<
    UserGeometryLayerVisibilityState,
    UserGeometryLayerVisibilityStateProps
  >(InjectionTokens.UserGeometryLayerVisibilityState)
  const userGeometryRepository = useInjection<IUserGeometryRepository>(InjectionTokens.UserGeometryRepository)
  const logger = useInjection<ILogger>(InjectionTokens.Logger)

  // Read `t` through a ref so the stable click handler (empty deps, by design)
  // always resolves the current-language unnamed-geometry fallback.
  const translateRef = useRef(t)
  translateRef.current = t

  const activeGeometries = mapLayerState.userGeometries

  const [renderDataById, setRenderDataById] = useState<Record<string, UserGeometryRenderData>>({})
  const [reloadToken, setReloadToken] = useState(0)
  // Bumped by a focus request ("Mostrar no mapa") to re-run the framing effect.
  const [focusToken, setFocusToken] = useState(0)
  // Selection is mirrored to a ref so the click handler can stay stable (the
  // map layer components re-add their layers when callbacks change identity).
  const selectedFeatureIdRef = useRef<string | undefined>(getSelectedFeatureId())

  // Tracks which active geometries have already been framed, so we center the
  // map only when the user newly activates one — not on the geometries restored
  // from persisted state at boot (which would otherwise yank the camera on every
  // reload). Seeded on first render with whatever is already active; deactivating
  // a geometry clears its mark so re-activating it frames again.
  const framedGeometryIdsRef = useRef<Set<string>>()
  if (framedGeometryIdsRef.current === undefined) {
    framedGeometryIdsRef.current = new Set(mapLayerState.userGeometries.map((geometry) => geometry.id))
  }

  // "Mostrar no mapa" from My Content: forget that the geometry was framed so the
  // framing effect centers it again — right away when its render data is loaded,
  // or as soon as it is (a geometry that was just turned on).
  useEffect(() => {
    const subscription = UserGeometryFocusRequestEvent.subscribe((id) => {
      framedGeometryIdsRef.current.delete(id)
      setFocusToken((token) => token + 1)
    })

    return () => subscription.unsubscribe()
  }, [])

  // Reload geometry data when the repository signals a change or a sync.
  useEffect(() => {
    const changedSubscription = UserGeometryChanged.subscribe(() => setReloadToken((token) => token + 1))
    const syncedSubscription = UserGeometrySynced.subscribe(() => setReloadToken((token) => token + 1))

    return () => {
      changedSubscription.unsubscribe()
      syncedSubscription.unsubscribe()
    }
  }, [])

  // Reconcile the cached active-geometry names with the repository. The name is
  // copied into MapLayerState at toggle-time and that copy is persisted, so a
  // later rename would otherwise leave the map/InfoPanel title stale (even across
  // reloads). Re-sync on mount and on every change/sync signal.
  useEffect(() => {
    let cancelled = false

    const reconcileNames = async () => {
      const result = await userGeometryRepository.getGeometries()
      if (cancelled || result.isFailure) return

      const nameById = new Map(result.getValue().map((geometry) => [geometry.id.toString(), geometry.name]))

      mapLayerState.userGeometries.forEach((info) => {
        const freshName = nameById.get(info.id)
        if (freshName && freshName !== info.name) {
          mapLayerStateMutator.renameActiveUserGeometry(info.id, freshName)
        }
      })
    }

    reconcileNames().catch((error) => {
      logger.warn('MapUserGeometriesPresenter', `Failed to reconcile geometry names: ${error}`)
    })

    return () => {
      cancelled = true
    }
  }, [reloadToken, mapLayerState.userGeometries, userGeometryRepository, mapLayerStateMutator, logger])

  // Load the .fgb render data for every active geometry.
  useEffect(() => {
    let cancelled = false

    const loadAll = async () => {
      const entries = await Promise.all(
        activeGeometries.map(async (info) => {
          const hiddenLayerIndices = layerVisibilityState.getHiddenIndices(info.id)
          const renderData = await loadUserGeometryRenderData(userGeometryRepository, info, hiddenLayerIndices)
          return renderData ? ([info.id, renderData] as const) : null
        })
      )

      if (cancelled) return

      const next: Record<string, UserGeometryRenderData> = {}
      entries.forEach((entry) => {
        if (entry) next[entry[0]] = entry[1]
      })
      setRenderDataById(next)
    }

    loadAll().catch((error) => {
      logger.warn('MapUserGeometriesPresenter', `Failed to load user geometries: ${error}`)
    })

    return () => {
      cancelled = true
    }
    // `layerVisibility.hiddenByGeometry` is in the deps so toggling a layer's eye
    // re-runs the load and re-filters (the .fgb comes back from the LRU cache, so
    // this is cheap); `layerVisibilityState` is a stable singleton.
  }, [
    activeGeometries,
    reloadToken,
    userGeometryRepository,
    logger,
    layerVisibility.hiddenByGeometry,
    layerVisibilityState
  ])

  // Center the map on a geometry's extent the first time it becomes active (or
  // when "Mostrar no mapa" asks for it), in the free area of the map (discounting
  // the open panels). Runs once its render data (and therefore its bounds) is
  // loaded; the ref seeded on mount keeps boot-restored geometries from triggering
  // a fly.
  useEffect(() => {
    const framed = framedGeometryIdsRef.current
    const activeIds = new Set(activeGeometries.map((info) => info.id))

    // Forget geometries that were turned off so re-activating frames them again.
    framed.forEach((id) => {
      if (!activeIds.has(id)) framed.delete(id)
    })

    activeGeometries.forEach((info) => {
      if (framed.has(info.id)) return

      const renderData = renderDataById[info.id]
      if (!renderData) return

      framed.add(info.id)
      if (renderData.bounds) frameBounds(renderData.bounds, getFeatureFlyToPadding())
    })
  }, [activeGeometries, renderDataById, focusToken])

  // Keep the local selection in sync with the panel so external closes (ESC,
  // map click, another feature, presenter remount) converge on the panel.
  useEffect(() => {
    const subscription = FeatureInfoState.subscribe((state) => {
      selectedFeatureIdRef.current =
        state.active?.type === 'user-geometry'
          ? String(state.active.feature?.properties?.id ?? state.active.feature?.id ?? '')
          : undefined
    })

    return () => subscription.unsubscribe()
  }, [])

  // Close the panel once no geometry is active anymore.
  useEffect(() => {
    if (activeGeometries.length > 0) return
    if (FeatureInfoState.getValue().active?.type !== 'user-geometry') return

    selectedFeatureIdRef.current = undefined
    FeatureInfoStateMutator.hide()
    ScreenElementsStateMutator.setFeatureInfoPanel(false)
  }, [activeGeometries])

  const handleFeatureClick = useCallback((feature: GeoJSON.Feature, geometryName: string) => {
    const featureId = getFeatureId(feature)

    if (featureId !== '' && selectedFeatureIdRef.current === featureId) {
      selectedFeatureIdRef.current = undefined
      FeatureInfoStateMutator.hide()
      ScreenElementsStateMutator.setFeatureInfoPanel(false)
      return
    }

    selectedFeatureIdRef.current = featureId
    FeatureInfoStateMutator.showUserGeometry(
      resolveFeatureTitle(feature, geometryName, translateRef.current('USER-GEOMETRIES_UNNAMED-GEOMETRY')),
      feature
    )
    ScreenElementsStateMutator.setFeatureInfoPanel(true)
    frameFeature(feature)
  }, [])

  if (activeGeometries.length === 0) return null

  return (
    <>
      {activeGeometries.map((info) => {
        const renderData = renderDataById[info.id]
        if (!renderData) return null

        return (
          <MapGeometries
            key={info.id}
            geometryId={info.id}
            geometryName={info.name}
            renderData={renderData}
            onFeatureClick={handleFeatureClick}
          />
        )
      })}
    </>
  )
}

export { MapUserGeometriesPresenter }
